import {
  createRoutesFromChildren,
  matchRoutes,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { mgPrisConfig, mgPrisRoutes } from '@retail/mgpris/config';
import {
  AdministrationPage,
  AppLayout,
  ApprovalCalculusPage,
  ApprovalPage,
  BaseAssortmentPage,
  BrandCampaignDetailsPage,
  CalculusPage,
  CampaignsPage,
  CentralAssortmentMemberViewPage,
  CentralAssortmentPage,
  CompetitorsPage,
  DashboardPage,
  DiscountsPage,
  ExportPage,
  FeatureTogglesPage,
  FixedPricePage,
  MarketInsightPage,
  MemberCampaignDetailsPage,
  MemberConfigPage,
  MgItemPage,
  MgSupplierItemSearchPage,
  MonitoringAssortmentPage,
  NotFoundPage,
  PermissionsPage,
  PriceControlPage,
  PriceControlSupplierPage,
  PriceRoundingPage,
  SubAssortmentsPage,
  SupplierItemInsightReportPage,
  SuppliersPage,
  useNavRoutes,
} from '@retail/app/pages';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo } from 'react';
import { BaseOutlet } from './BaseOutlet';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { isRunnningLocally } from './isRunningLocally';

// init Sentry in the same file as Sentry.withSentryReactRouterV6Routing
// see https://github.com/getsentry/sentry-javascript/issues/5309
if (mgPrisConfig.sentryDsn && !isRunnningLocally) {
  Sentry.init({
    dsn: mgPrisConfig.sentryDsn,
    environment: mgPrisConfig.env,
    release: mgPrisConfig.version,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
  });
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const AppRoutes: React.FC = () => {
  const { userCanRead } = usePermissionsForCurrentUser();
  const priceRoundingFeatureEnabled = useIsFeatureEnabled('price-rounding');
  const priceControlFeatureEnabled = useIsFeatureEnabled('price-control');
  const dashboardEnabled = useIsFeatureEnabled('new-dashboard');
  const discountMatrixFeatureEnabled = useIsFeatureEnabled('discount-matrix');
  const { isBrandContext } = useSelectedOrgUnit();

  const showDashboard = useMemo(() => userCanRead('DASHBOARD') && dashboardEnabled, [userCanRead]);
  const showBaseAssortment = useMemo(() => userCanRead('ASSORTMENT'), [userCanRead]);
  const showCentralAssortment = useMemo(() => userCanRead('CENTRAL_ASSORTMENT'), [userCanRead]);
  const showCentralAssortmentMemberView = useMemo(
    () => userCanRead('CENTRAL_ASSORTMENT_MEMBER_VIEW'),
    [userCanRead]
  );
  const showSubAssortments = useMemo(() => userCanRead('SUB_ASSORTMENTS'), [userCanRead]);
  const showPriceCalculation = useMemo(() => userCanRead('PRICE_CALCULATION'), [userCanRead]);
  const showDiscounts = useMemo(
    () => userCanRead('PRICE_CALCULATION__CUSTOMER_MARKUP') && discountMatrixFeatureEnabled,
    [userCanRead, discountMatrixFeatureEnabled]
  );
  const showItemSearch = useMemo(() => userCanRead('ITEM_SEARCH'), [userCanRead]);
  const showMarketInsight = useMemo(() => userCanRead('MARKET_INSIGHT'), [userCanRead]);
  const showAdministration = useMemo(() => userCanRead('ADMINISTRATION'), [userCanRead]);
  const showItemsExport = useMemo(() => userCanRead('ITEM_EXPORT'), [userCanRead]);
  const showApproval = useMemo(() => userCanRead('APPROVAL'), [userCanRead]);
  const showPriceControl = useMemo(
    () => userCanRead('PRICE_CONTROL') && priceControlFeatureEnabled,
    [priceControlFeatureEnabled, userCanRead]
  );
  const showFeatureToggles = useMemo(() => userCanRead('FEATURE_TOGGLES'), [userCanRead]);
  const showPermissions = useMemo(() => userCanRead('PERMISSIONS'), [userCanRead]);
  const showCampaigns = useMemo(() => userCanRead('CAMPAIGN'), [userCanRead]);
  const showSupplierItemsInsightReport = useMemo(
    () => userCanRead('SUPPLIER_ITEMS_INSIGHT_REPORT'),
    [userCanRead]
  );
  const showRounding = useMemo(
    () => priceRoundingFeatureEnabled && userCanRead('ROUNDING'),
    [userCanRead, priceRoundingFeatureEnabled]
  );
  const showMemberConfig = useMemo(() => userCanRead('MEMBER_CONFIG'), [userCanRead]);

  const navRoutes = useNavRoutes();

  return (
    <SentryRoutes>
      <Route
        path={mgPrisRoutes.root.fullRoutePath}
        element={
          <AppLayout>
            <Outlet />
          </AppLayout>
        }
      >
        {/* All following routes are only shown to authorized users, due to AuthContainerPage */}
        <Route
          path={mgPrisRoutes.root.fullRoutePath}
          element={
            <BaseOutlet
              rootPath={mgPrisRoutes.root.getFullLinkPath()}
              fallbackPath={navRoutes[0].to ?? ''}
            />
          }
        >
          {showDashboard && (
            <Route path={mgPrisRoutes.dashboard.fullRoutePath} element={<DashboardPage />} />
          )}
          {showBaseAssortment && (
            <Route path={mgPrisRoutes.assortment.fullRoutePath} element={<BaseAssortmentPage />} />
          )}
          {showCentralAssortment && (
            <Route
              path={mgPrisRoutes.assortment.fullRoutePath}
              element={<CentralAssortmentPage />}
            />
          )}

          {showPriceCalculation && (
            <>
              <Route path={mgPrisRoutes.calculus.fullRoutePath} element={<CalculusPage />} />
              <Route
                path={mgPrisRoutes.calculus.calculusArea.fullRoutePath}
                element={<CalculusPage />}
              />
            </>
          )}

          {showDiscounts && (
            <>
              <Route path={mgPrisRoutes.discounts.fullRoutePath} element={<DiscountsPage />} />
              <Route
                path={mgPrisRoutes.discounts.discountArea.fullRoutePath}
                element={<DiscountsPage />}
              />
            </>
          )}

          {showItemSearch && (
            <>
              <Route
                path={mgPrisRoutes.items.fullRoutePath}
                element={<MgSupplierItemSearchPage />}
              />
              <Route path={mgPrisRoutes.items.item.fullRoutePath} element={<MgItemPage />} />
            </>
          )}

          {showMarketInsight && (
            <Route path={mgPrisRoutes.marketInsight.fullRoutePath} element={<MarketInsightPage />}>
              <Route
                path={mgPrisRoutes.marketInsight.assortments.fullRoutePath}
                element={<MonitoringAssortmentPage />}
              />
              <Route
                path={mgPrisRoutes.marketInsight.assortments.assortment.fullRoutePath}
                element={<MonitoringAssortmentPage />}
              />
              <Route
                path={mgPrisRoutes.marketInsight.competitors.fullRoutePath}
                element={<CompetitorsPage />}
              />
            </Route>
          )}

          {showAdministration && (
            <Route
              path={mgPrisRoutes.administration.fullRoutePath}
              element={<AdministrationPage administrationRoute={mgPrisRoutes.administration} />}
            >
              {showRounding && (
                <Route
                  path={mgPrisRoutes.administration.rounding.fullRoutePath}
                  element={<PriceRoundingPage />}
                />
              )}
              <Route
                path={mgPrisRoutes.administration.fixedPrice.fullRoutePath}
                element={<FixedPricePage />}
              />
              <Route
                path={mgPrisRoutes.administration.suppliers.fullRoutePath}
                element={<SuppliersPage />}
              />
              {showSubAssortments && (
                <Route
                  path={mgPrisRoutes.administration.assortments.fullRoutePath}
                  element={<SubAssortmentsPage />}
                />
              )}
            </Route>
          )}

          {showItemsExport && (
            <Route path={mgPrisRoutes.export.fullRoutePath} element={<ExportPage />} />
          )}

          {showPriceControl && (
            <>
              <Route
                path={mgPrisRoutes.priceControl.fullRoutePath}
                element={<PriceControlPage />}
              />
              <Route
                path={mgPrisRoutes.priceControl.supplier.fullRoutePath}
                element={<PriceControlSupplierPage />}
              />
            </>
          )}
          {showApproval && (
            <>
              <Route path={mgPrisRoutes.approval.fullRoutePath} element={<ApprovalPage />} />
              <Route
                path={mgPrisRoutes.approval.calculus.fullRoutePath}
                element={<ApprovalCalculusPage />}
              />
            </>
          )}
          {showMemberConfig && (
            <Route path={mgPrisRoutes.memberConfig.fullRoutePath} element={<MemberConfigPage />} />
          )}

          {showFeatureToggles && (
            <Route
              path={mgPrisRoutes.superuser.features.fullRoutePath}
              element={<FeatureTogglesPage />}
            />
          )}

          {showPermissions && (
            <Route
              path={mgPrisRoutes.superuser.permissions.fullRoutePath}
              element={<PermissionsPage />}
            />
          )}

          {showCampaigns && (
            <>
              <Route path={mgPrisRoutes.campaigns.fullRoutePath} element={<CampaignsPage />} />
              <Route
                path={mgPrisRoutes.campaigns.campaignDetails.fullRoutePath}
                element={
                  isBrandContext ? <BrandCampaignDetailsPage /> : <MemberCampaignDetailsPage />
                }
              />
              <Route
                path={mgPrisRoutes.campaigns.campaignType.campaignDetails.fullRoutePath}
                element={<MemberCampaignDetailsPage />}
              />
            </>
          )}

          {showCentralAssortmentMemberView && (
            <Route
              path={mgPrisRoutes.centralAssortments.fullRoutePath}
              element={<CentralAssortmentMemberViewPage />}
            />
          )}
          {showSupplierItemsInsightReport && (
            <Route
              path={mgPrisRoutes.insightReport.fullRoutePath}
              element={<SupplierItemInsightReportPage />}
            />
          )}
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </SentryRoutes>
  );
};
